// pages/point/activity-log.tsx

import React from 'react';
import TopBackButton from '@/component/common/top-back-button';
import SwitchWithText from '@/component/point/switch-with-text';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { SFPalette } from '@/lib/color';
import Typography from '@mui/material/Typography';
import SFArrowDropdownIcon from '@/component/icons/sf-arrow-dropdown-icon';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Popover from '@mui/material/Popover';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { fontWeight } from '@/theme';
import HistoryList from '@/component/users/point/HistoryList';

export default function UsageDetails() {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [anchorElSecond, setAnchorElSecond] = React.useState<HTMLButtonElement | null>(null);
  const [filter, setFilter] = React.useState('전체');
  const [sortOrder, setSortOrder] = React.useState('최신순');
  const [timePeriod, setTimePeriod] = React.useState('1개월');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClickSecond = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElSecond(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseSecond = () => {
    setAnchorElSecond(null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter((event.target as HTMLInputElement).value);
  };

  const handleSortOrderChange = (order: string) => {
    setSortOrder(order);
  };

  const handleTimePeriodChange = (period: string) => {
    setTimePeriod(period);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'popover' : undefined;
  const openSecond = Boolean(anchorElSecond);
  const idSecond = openSecond ? 'second-popover' : undefined;

  const theme = useTheme();
  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '812px',
        height: '100vh',
        position: 'relative',
      }}
    >
      <TopBackButton
        title="이용내역"
        boxProps={{
          mb: 4,
        }}
      />
      <SwitchWithText firstText="결제" secondText="포인트" />
      <Stack direction="row" justifyContent="space-between" pt={2}>
        <Button endIcon={<SFArrowDropdownIcon />} color="secondary" onClick={handleClick}>
          <Typography color={SFPalette.grey4} variant="subtitle2">
            {filter}
          </Typography>
        </Button>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              width: '100%',
              borderRadius: 4,
              padding: theme.spacing(3),
            },
            elevation: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={fontWeight.Bold} mb={2}>
            내역 유형
          </Typography>
          <RadioGroup
            row={true}
            aria-label="filter"
            name="filter"
            value={filter}
            onChange={handleChange}
            sx={{ justifyContent: 'space-between', mb: 2 }}
          >
            <FormControlLabel
              value="전체"
              control={<Radio />}
              label={<Typography variant="subtitle2">전체</Typography>}
            />
            <FormControlLabel
              value="결제 완료"
              control={<Radio />}
              label={<Typography variant="subtitle2">결제 완료</Typography>}
            />
            <FormControlLabel
              value="결제 취소"
              control={<Radio />}
              label={<Typography variant="subtitle2">결제 취소</Typography>}
            />
          </RadioGroup>
          <Button
            variant="contained"
            color="primary"
            onClick={handleClose}
            sx={{ width: '100%', padding: theme.spacing(1) }}
          >
            확인
          </Button>
        </Popover>
        <Button
          endIcon={<SFArrowDropdownIcon />}
          color="secondary"
          onClick={handleClickSecond}
          sx={[
            {
              justifyContent: 'space-between',
              '& .MuiButton-endIcon': {
                marginLeft: 0,
              },
            },
          ]}
        >
          <Typography color={SFPalette.grey4} variant="subtitle2">
            {`${sortOrder}·${timePeriod}`}
          </Typography>
        </Button>
        <Popover
          id={idSecond}
          open={openSecond}
          anchorEl={anchorElSecond}
          onClose={handleCloseSecond}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              width: '100%',
              borderRadius: 4,
              padding: theme.spacing(3),
            },
            elevation: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={fontWeight.Bold} mb={3}>
            조회 유형
          </Typography>
          <Typography color={SFPalette.grey4} variant="subtitle2" mb={2}>
            정렬 기준
          </Typography>
          <Stack direction="row" mb={2} gap={1}>
            <Button
              variant={sortOrder === '최신순' ? 'contained' : 'outlined'}
              color={sortOrder === '최신순' ? 'primary' : 'secondary'}
              onClick={() => handleSortOrderChange('최신순')}
            >
              최신순
            </Button>
            <Button
              variant={sortOrder === '과거순' ? 'contained' : 'outlined'}
              color={sortOrder === '과거순' ? 'primary' : 'secondary'}
              onClick={() => handleSortOrderChange('과거순')}
            >
              과거순
            </Button>
          </Stack>
          <Typography color={SFPalette.grey4} variant="subtitle2" mb={2}>
            기간 설정
          </Typography>
          <Stack direction="row" justifyContent="space-between" mb={3}>
            <Button
              variant={timePeriod === '1개월' ? 'contained' : 'outlined'}
              color={timePeriod === '1개월' ? 'primary' : 'secondary'}
              onClick={() => handleTimePeriodChange('1개월')}
            >
              1개월
            </Button>
            <Button
              variant={timePeriod === '3개월' ? 'contained' : 'outlined'}
              color={timePeriod === '3개월' ? 'primary' : 'secondary'}
              onClick={() => handleTimePeriodChange('3개월')}
            >
              3개월
            </Button>
            <Button
              variant={timePeriod === '6개월' ? 'contained' : 'outlined'}
              color={timePeriod === '6개월' ? 'primary' : 'secondary'}
              onClick={() => handleTimePeriodChange('6개월')}
            >
              6개월
            </Button>
            <Button
              variant={timePeriod === '직접입력' ? 'contained' : 'outlined'}
              color={timePeriod === '직접입력' ? 'primary' : 'secondary'}
              onClick={() => handleTimePeriodChange('직접입력')}
            >
              직접입력
            </Button>
          </Stack>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCloseSecond}
            sx={{ width: '100%', padding: theme.spacing(1) }}
          >
            확인
          </Button>
        </Popover>
      </Stack>
      <Divider
        sx={{
          mt: 2,
        }}
      />
      <HistoryList />
    </Container>
  );
}
